import first from 'lodash/first';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import type { IBetBuilderMarket, IBetBuilderSelection, IMarketGroup } from '@/interface/betBuilder';
import { BetBuilderDisplayType, BetBuilderMarket, BetBuilderSelectionType, BetBuilderStatType } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';

export interface IMarketRuleGroup {
    title?: string;
    selections: IBetBuilderSelection[];
}

export enum MatchDetailLayout{
    column,
    row,
}

interface IRule {
    layout: MatchDetailLayout;
    shortNameDisplay: (selection: IBetBuilderSelection) => string;
    fullNameDisplay: (selection: IBetBuilderSelection) => string;
    marketTypeDisplay: () => string;
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => string;
    shortNameDisplayList: () => string[];
    fullNameDisplayList: () => string[];
    matchDetailLayout: (market: IBetBuilderMarket) => IMarketRuleGroup[];
}

const defaultRule: IRule = {
    layout: MatchDetailLayout.column,
    shortNameDisplay: (selection: IBetBuilderSelection) => selection.type.name,
    fullNameDisplay: (selection: IBetBuilderSelection) => selection.type.name,
    marketTypeDisplay: () => '',
    marketTypeWithStatTypeDisplay: () => '',
    matchDetailLayout: (market: IBetBuilderMarket) => [{
        selections: market.selections,
    }],
    shortNameDisplayList: () => [],
    fullNameDisplayList: () => [],
};

const correctScoreRule: Partial<IRule> = {
    layout: MatchDetailLayout.column,
    matchDetailLayout: (market: IBetBuilderMarket) => {
        const selections = market.selections.map((selection) => {
            const [home, away] = selection.type.name.split(':').map(x => parseInt(x));
            return {
                selection,
                homeScore: home,
                awayScore: away,
            };
        });

        return [{
            title: i18n.t('odds_option_home').toString(),
            selections: sortBy(
                selections.filter(p => p.homeScore > p.awayScore),
                [p => p.homeScore, p => p.awayScore],
            )
                .map(p => p.selection),
        }, {
            title: i18n.t('odds_option_draw').toString(),
            selections: sortBy(
                selections.filter(p => p.homeScore === p.awayScore),
                [p => p.homeScore, p => p.awayScore],
            )
                .map(p => p.selection),
        }, {
            title: i18n.t('odds_option_away').toString(),
            selections: sortBy(
                selections.filter(p => p.homeScore < p.awayScore),
                [p => p.awayScore, p => p.homeScore],
            )
                .map(p => p.selection),
        }];
    },
    marketTypeDisplay: () => i18n.t('marketType_CorrectScore_Name').toString(),
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => (displayType === BetBuilderDisplayType.FH_CorrectScore
        ? i18n.t('marketType_FH_CorrectScore').toString()
        : i18n.t('marketType_CorrectScore_Name').toString()),
};

const hdpRule: Partial<IRule> = {
    layout: MatchDetailLayout.row,
    shortNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Home) return i18n.t('odds_option_home_short').toString();
        if (selection.type.id === BetBuilderSelectionType.Away) return i18n.t('odds_option_away_short').toString();
        return selection.type.name;
    },
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Home) return i18n.t('odds_option_home').toString();
        if (selection.type.id === BetBuilderSelectionType.Away) return i18n.t('odds_option_away').toString();
        return selection.type.name;
    },
    marketTypeDisplay: () => i18n.t('marketType_Handicap_Name').toString(),
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => (displayType === BetBuilderDisplayType.FH_Handicap
        ? i18n.t('marketType_FH_Handicap').toString()
        : i18n.t('marketType_Handicap_Name').toString()),
    shortNameDisplayList: () => [
        i18n.t('odds_option_home_short').toString(),
        i18n.t('odds_option_away_short').toString(),
    ],
    fullNameDisplayList: () => [
        i18n.t('odds_option_home').toString(),
        i18n.t('odds_option_away').toString(),
    ],
};

const _1x2Rule: Partial<IRule> = {
    layout: MatchDetailLayout.column,
    matchDetailLayout: (market: IBetBuilderMarket) => [{
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType._1),
    }, {
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType._2),
    }, {
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType.X),
    }],
    shortNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType._1) return i18n.t('odds_option_home_short').toString();
        if (selection.type.id === BetBuilderSelectionType._2) return i18n.t('odds_option_away_short').toString();
        if (selection.type.id === BetBuilderSelectionType.X) return i18n.t('odds_option_draw_short').toString();
        return selection.type.name;
    },
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType._1) return i18n.t('odds_option_home').toString();
        if (selection.type.id === BetBuilderSelectionType._2) return i18n.t('odds_option_away').toString();
        if (selection.type.id === BetBuilderSelectionType.X) return i18n.t('odds_option_draw').toString();
        return selection.type.name;
    },
    marketTypeDisplay: () => i18n.t('marketType__1X2_Name').toString(),
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => (displayType === BetBuilderDisplayType.FH_1X2
        ? i18n.t('marketType_FH_1X2').toString()
        : i18n.t('marketType__1X2_Name').toString()),
    shortNameDisplayList: () => [
        i18n.t('odds_option_home_short').toString(),
        i18n.t('odds_option_away_short').toString(),
        i18n.t('odds_option_draw_short').toString(),
    ],
    fullNameDisplayList: () => [
        i18n.t('odds_option_home').toString(),
        i18n.t('odds_option_away').toString(),
        i18n.t('odds_option_draw').toString(),
    ],

};

const overUnderRule: Partial<IRule> = {
    layout: MatchDetailLayout.column,
    matchDetailLayout: (market: IBetBuilderMarket) => [{
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType.Over),
    }, {
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType.Under),
    }],
    shortNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Over) return i18n.t('odds_option_over_short').toString();
        if (selection.type.id === BetBuilderSelectionType.Under) return i18n.t('odds_option_under_short').toString();
        return selection.type.name;
    },
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Over) return i18n.t('odds_option_over').toString();
        if (selection.type.id === BetBuilderSelectionType.Under) return i18n.t('odds_option_under').toString();
        return selection.type.name;
    },
    marketTypeDisplay: () => i18n.t('marketType_OverUnder_Name').toString(),
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => (displayType === BetBuilderDisplayType.FH_OverUnder
        ? i18n.t('marketType_FH_OverUnder').toString()
        : i18n.t('marketType_OverUnder_Name').toString()),
    shortNameDisplayList: () => [
        i18n.t('odds_option_over_short').toString(),
        i18n.t('odds_option_under_short').toString(),
    ],
    fullNameDisplayList: () => [
        i18n.t('odds_option_over').toString(),
        i18n.t('odds_option_under').toString(),
    ],
};

const oddEvenRule: Partial<IRule> = {
    layout: MatchDetailLayout.column,
    matchDetailLayout: (market: IBetBuilderMarket) => [{
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType.Odd),
    }, {
        selections: market.selections.filter(x => x.type.id === BetBuilderSelectionType.Even),
    }],
    shortNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Odd) return i18n.t('odds_option_odd_short').toString();
        if (selection.type.id === BetBuilderSelectionType.Even) return i18n.t('odds_option_even_short').toString();
        return selection.type.name;
    },
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType.Odd) return i18n.t('odds_option_odd').toString();
        if (selection.type.id === BetBuilderSelectionType.Even) return i18n.t('odds_option_even').toString();
        return selection.type.name;
    },
    marketTypeDisplay: () => i18n.t('marketType_OddEven_Name').toString(),
    marketTypeWithStatTypeDisplay: (displayType: BetBuilderDisplayType) => (displayType === BetBuilderDisplayType.FH_OddEven
        ? i18n.t('marketType_FH_OddEven').toString()
        : i18n.t('marketType_OddEven_Name').toString()),
    shortNameDisplayList: () => [
        i18n.t('odds_option_odd_short').toString(),
        i18n.t('odds_option_even_short').toString(),
    ],
    fullNameDisplayList: () => [
        i18n.t('odds_option_odd').toString(),
        i18n.t('odds_option_even').toString(),
    ],
};
const totalGoalRule: Partial<IRule> = {
    layout: MatchDetailLayout.row,
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.name === '7-over') {
            return i18n.t('odds_option_tg_7_up').toString();
        }

        return selection.type.name;
    },
};
const doubleChanceRule: Partial<IRule> = {
    layout: MatchDetailLayout.row,
    marketTypeDisplay: () => i18n.t('marketType_DoubleChance_Name').toString(),
    shortNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType._1X) return i18n.t('odds_option_dc_1x').toString();
        if (selection.type.id === BetBuilderSelectionType._12) return i18n.t('odds_option_dc_12').toString();
        if (selection.type.id === BetBuilderSelectionType._2X) return i18n.t('odds_option_dc_2x').toString();
        return selection.type.name;
    },
    fullNameDisplay: (selection: IBetBuilderSelection) => {
        if (selection.type.id === BetBuilderSelectionType._1X) return i18n.t('odds_option_dc_1x').toString();
        if (selection.type.id === BetBuilderSelectionType._12) return i18n.t('odds_option_dc_12').toString();
        if (selection.type.id === BetBuilderSelectionType._2X) return i18n.t('odds_option_dc_2x').toString();
        return selection.type.name;
    },
    matchDetailLayout: (market: IBetBuilderMarket) => [{
        selections: [
            ...market.selections.filter(x => x.type.id === BetBuilderSelectionType._1X),
            ...market.selections.filter(x => x.type.id === BetBuilderSelectionType._12),
            ...market.selections.filter(x => x.type.id === BetBuilderSelectionType._2X),
        ],
    }],
};

const rulesConfig: Partial<Record<BetBuilderDisplayType, Partial<IRule>>> = {
    [BetBuilderDisplayType.Handicap]: hdpRule,
    [BetBuilderDisplayType.FH_Handicap]: hdpRule,
    [BetBuilderDisplayType._1X2]: _1x2Rule,
    [BetBuilderDisplayType.FH_1X2]: _1x2Rule,
    [BetBuilderDisplayType.OverUnder]: overUnderRule,
    [BetBuilderDisplayType.FH_OverUnder]: overUnderRule,
    [BetBuilderDisplayType.OddEven]: oddEvenRule,
    [BetBuilderDisplayType.FH_OddEven]: oddEvenRule,
    [BetBuilderDisplayType.CorrectScore]: correctScoreRule,
    [BetBuilderDisplayType.FH_CorrectScore]: correctScoreRule,
    [BetBuilderDisplayType.DoubleChance]: doubleChanceRule,
    [BetBuilderDisplayType.TotalGoal]: totalGoalRule,
};

const rulesConfigByMarketType: Partial<Record<BetBuilderMarket, Partial<IRule>>> = {
    [BetBuilderMarket.SetCorrectScore]: correctScoreRule,
    [BetBuilderMarket.Set1x2]: _1x2Rule,
    [BetBuilderMarket.SetTotalGoalsOverUnder]: overUnderRule,
    [BetBuilderMarket.SetTotalGoalsOddEven]: oddEvenRule,
    [BetBuilderMarket.SetDoubleChance]: doubleChanceRule,
    [BetBuilderMarket.SetHandicap]: hdpRule,
};

export const getMarketRule = (displayType: BetBuilderDisplayType): IRule => ({
    ...defaultRule,
    ...rulesConfig[displayType],
});

export const getMarketRuleByMarketType = (marketType: BetBuilderMarket): IRule => ({
    ...defaultRule,
    ...rulesConfigByMarketType[marketType],
});

const singleColumnDisplay: Array<BetBuilderDisplayType> = [
    BetBuilderDisplayType.Unknown,
    BetBuilderDisplayType.TotalGoal,
    BetBuilderDisplayType.HTFT,
    BetBuilderDisplayType.MoneyLine,
    BetBuilderDisplayType.FGLG,
    BetBuilderDisplayType.LiveScore,
    BetBuilderDisplayType.FH_LiveScore,
    BetBuilderDisplayType.Game,
    BetBuilderDisplayType.OutRight,
    BetBuilderDisplayType.MixParlay,
];

export function isSingleColumnDisplay(displayType: BetBuilderDisplayType): boolean {
    return singleColumnDisplay.includes(displayType);
}

const twoPerRowDisplay: Array<BetBuilderDisplayType> = [
    BetBuilderDisplayType.TotalGoal,
];

export function isTwoPerRowDisplay(displayType: BetBuilderDisplayType): boolean {
    return twoPerRowDisplay.includes(displayType);
}

export function groupMoreMarkets(markets: IBetBuilderMarket[]) {
    const groupData = groupBy(markets, market => `${market.marketType.id}-${market.marketType.name}-${market.matchStatType.id}`);

    const marketGroups: IMarketGroup[] = Object.keys(groupData).map(key => ({
        key,
        title: first(groupData[key])?.marketType.name ?? '',
        markets: groupData[key],
    }));

    return marketGroups;
}

const marketOrder = [
    BetBuilderDisplayType.Handicap,
    BetBuilderDisplayType.OddEven,
    BetBuilderDisplayType.OverUnder,
    BetBuilderDisplayType.CorrectScore,
    BetBuilderDisplayType._1X2,
    BetBuilderDisplayType.TotalGoal,
    BetBuilderDisplayType.FH_Handicap,
    BetBuilderDisplayType.FH_OverUnder,
    BetBuilderDisplayType.FH_1X2,
    BetBuilderDisplayType.HTFT,
    BetBuilderDisplayType.MoneyLine,
    BetBuilderDisplayType.FH_OddEven,
    BetBuilderDisplayType.FGLG,
    BetBuilderDisplayType.FH_CorrectScore,
    BetBuilderDisplayType.DoubleChance,
    BetBuilderDisplayType.LiveScore,
    BetBuilderDisplayType.FH_LiveScore,
    BetBuilderDisplayType.Game,
    BetBuilderDisplayType.Unknown,
];

const statTypeOrder = [
    BetBuilderStatType.Unknown,
    BetBuilderStatType.FullTime,
    BetBuilderStatType.FirstHalf,
    BetBuilderStatType.SecondHalf,
];

export const orderMarkets = (markets: IBetBuilderMarket[]) => orderBy(
    markets,
    [
        market => (marketOrder.includes(market.displayType) ? marketOrder.indexOf(market.displayType) : Infinity),
        market => (statTypeOrder.includes(market.matchStatType.id) ? statTypeOrder.indexOf(market.matchStatType.id) : Infinity),
        market => market.matchMarketId,
    ],
);
